import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusTimeline } from "@/components/StatusTimeline";
import { OrderItems } from "@/components/OrderItems";
import { ArtFileList } from "@/components/ArtFileList";
import { api, brl, fmtDate, formatError, paymentLabel, paymentStatusLabel, STATUSES } from "@/lib/api";
import { CashInfo } from "@/components/CashPayment";
import { WhatsAppNotifyButton } from "@/components/WhatsAppNotifyButton";
import { usePickupTemplate } from "@/hooks/usePickupTemplate";
import { NoteButton } from "@/components/NoteButton";
import { OrderDeleteActions } from "@/components/OrderDeleteActions";

const PICKUP = "Disponível para retirada";

export function OrderEditDialog({ order, onClose, onSaved, onDeleted }) {
  const [status, setStatus] = useState(order.status);
  const [busy, setBusy] = useState(false);
  const c = order.customer, d = order.delivery;
  const { pickup_message: template } = usePickupTemplate();

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, { status });
      toast.success(data.status === PICKUP && order.status !== PICKUP ? "Status salvo. Agora avise o cliente pelo WhatsApp." : `Pedido #${order.number} atualizado`);
      onSaved(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="order-edit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-mono">Pedido #{order.number}{order.customer_type === "revendedor" && <span className="rounded-full bg-violet-100 px-2 py-0.5 font-sans text-xs font-bold text-violet-800">Revendedor</span>}</DialogTitle></DialogHeader>
        <div className="space-y-6">
          <StatusTimeline order={order} />
          <div className="grid gap-4 rounded-md bg-slate-50 p-4 text-sm sm:grid-cols-2">
            <div>
              <p className="font-semibold">{c.name}</p>
              <p className="text-slate-600">{c.email} · {c.phone}</p>
              <p className="text-xs text-slate-500">{fmtDate(order.created_at)}</p>
            </div>
            <div className="text-slate-600">
              <p className="font-semibold text-slate-900">{d.method === "retirada" ? "Retirada na loja" : "Entrega"}</p>
              {d.method === "entrega" && <p>{d.address}, {d.number} {d.complement} — {d.district} · {d.city}/{d.state} · CEP {d.cep}</p>}
            </div>
            {order.notes && <p className="sm:col-span-2"><span className="font-semibold">Observações:</span> {order.notes}</p>}
          </div>
          <OrderItems items={order.items} />
          <div className="space-y-2" data-testid="order-art-files">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Arquivos de arte</p>
            {order.arts_deleted_at
              ? <p className="text-sm text-slate-500" data-testid="arts-deleted-info">Artes apagadas em {fmtDate(order.arts_deleted_at)}</p>
              : <ArtFileList files={order.art_files} />}
          </div>
          <p className="text-right font-mono text-xl font-bold">{brl(order.total)} <span className="text-xs font-normal text-slate-500">· {paymentStatusLabel(order.payment_status)} ({paymentLabel(order.payment_method)})</span></p>
          <CashInfo order={order} />
          <div className="grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Status do pedido</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-11" data-testid="select-order-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s, i) => <SelectItem key={s} value={s} data-testid={`order-status-option-${i}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {order.status === PICKUP && (
              <div className="flex flex-col items-end justify-end gap-1">
                <WhatsAppNotifyButton order={order} template={template} onNotified={onSaved} />
                <p className="text-xs text-slate-500" data-testid="notified-info">{order.notified_at ? `Cliente avisado em ${fmtDate(order.notified_at)}` : "Cliente ainda não avisado"}</p>
              </div>
            )}
          </div>
          {order.status === PICKUP && <OrderDeleteActions order={order} onUpdated={onSaved} onDeleted={onDeleted} />}
          <div className="flex flex-wrap justify-end gap-2">
            <NoteButton order={order} />
            <Button variant="outline" onClick={onClose} data-testid="btn-close-order">Fechar</Button>
            <Button onClick={save} disabled={busy} className="bg-[#0099DD] hover:bg-sky-700" data-testid="btn-save-order">{busy ? "Salvando..." : "Salvar alterações"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
